import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Stack,
  UseDisclosureReturn,
  useToast,
} from '@chakra-ui/react';
import { backlogStatusOptions } from '@pelicargo/utils';
import { Backlog } from '@prisma/client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { trpc } from '../../config/trpc';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { AirportInput } from '../Autocomplete/AirportInput';
import { SimpleField } from '../SimpleField';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { SimpleSelect } from '../SimpleSelect';

type Props = {
  disclosure: UseDisclosureReturn;
  selected: Backlog;
};

export const BacklogDrawer = ({ disclosure, selected }: Props) => {
  const toast = useToast();
  const methods = useForm<Backlog>({
    defaultValues: {},
  });
  const [isUpserting, setIsUpserting] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    if (!selected) {
      methods.reset({
        provider_id: null,
        airport_id: null,
        expires_at: null,
        id: null,
        note: null,
        starts_at: null,
        status: null,
        next_flight_at: null,
      } satisfies Omit<Backlog, 'created_at' | 'updated_at'>);
      methods.unregister('id'); // just be safe, since it isn't exposed
    } else {
      methods.reset(selected);
    }
  }, [methods, selected]);

  const { mutateAsync } = trpc.upsertBacklog.useMutation();

  const onValid = async (data: Backlog) => {
    setIsUpserting(true);
    try {
      await mutateAsync(data);
      toast({
        title: 'Success',
        description: selected ? 'Backlog updated successfully' : 'Backlog created successfully',
        status: 'success',
      });
      disclosure.onClose();
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    } finally {
      setIsUpserting(false);
    }
  };

  const handleResolve = async () => {
    setIsResolving(true);
    try {
      await mutateAsync({
        ...selected,
        expires_at: new Date(),
      });
      toast({ title: 'Success', description: 'Backlog resolved successfully', status: 'success' });
      disclosure.onClose();
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    } finally {
      setIsResolving(false);
    }
  };

  const onError = (error: any) => {
    toast({ title: 'Error', description: error?.message, status: 'error' });
  };

  const handleSubmit = methods.handleSubmit(onValid, onError);

  return (
    <SimpleForm {...methods} submitOnBlur={false}>
      <form>
        <Drawer {...disclosure} size="md">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerHeader>
              <Heading>New Backlog</Heading>
            </DrawerHeader>
            <DrawerBody>
              <Stack spacing="4">
                <SimpleField name="provider_id" label="Airlines">
                  <AirlineInput name="provider_id" />
                </SimpleField>
                <SimpleField name="airport_id" label="Airport">
                  <AirportInput name="airport_id" />
                </SimpleField>
                <Stack direction="row" w="full">
                  <SimpleLabeledPopoverInput
                    name="starts_at"
                    label="Start"
                    inputType="date-picker"
                    flex={1}
                    showClearButton
                    showTime
                  />
                  <SimpleLabeledPopoverInput
                    name="expires_at"
                    label="End"
                    inputType="date-picker"
                    flex={1}
                    showClearButton
                    showTime
                  />
                  <SimpleLabeledPopoverInput
                    name="next_flight_at"
                    label="Next Flight"
                    inputType="date-picker"
                    showTime={false}
                    showClearButton
                    flex={1}
                  />
                </Stack>
                <SimpleSelect name="status" label="Status" options={backlogStatusOptions} />
                <SimpleInput name="note" label="Note" />
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <Stack direction="row" spacing="2" flex="1" justifyContent="flex-end">
                <Button colorScheme="gray" onClick={handleResolve} isDisabled={!selected} isLoading={isResolving}>
                  Resolve
                </Button>
                <Button colorScheme="primary" onClick={handleSubmit} isLoading={isUpserting}>
                  {selected ? 'Update' : 'Create'}
                </Button>
              </Stack>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </SimpleForm>
  );
};
